<template>
  <div @click="closeMultiSelect">
    <Modal @closeModal="$emit('closeTriggered')">
      <!-- MODAL TITLE -->
      <div slot="modal-title">
        <span class="font-weight-bold" v-if="modal_type === 'assign'"
          >Assign Subject(s)</span
        >
        <span class="font-weight-bold" v-else>Remove Subject(s)</span>
      </div>

      <!-- MODAL BODY -->
      <div slot="modal-body">
        <!-- ERROR ALERT -->
        <div style="position: relative; top: -30px; margin-bottom: -8px">
          <AuthAlertCard
            v-if="show_alert"
            :alert_type="alert_type"
            :alert_msg="alert_msg"
          ></AuthAlertCard>
        </div>

        <form action @submit.prevent="confirmSelections" autocomplete="off">
          <!-- TEACHER NAME -->
          <div class="modal-body-row row">
            <div
              class="user-info-section col-12 d-flex justify-content-start align-items-center nowrap mb-3"
            >
              <!-- AVATAR -->
              <div class="avatar avatar_lg brand_inverse_light_bg mr-3">
                <img
                  v-lazy="image"
                  :alt="name"
                  class="avatar-img"
                  v-if="image"
                />
                <div
                  class="avatar-text white-text font-weight-bold font-20"
                  :class="setAvatarBg"
                  v-else
                >
                  {{ getInitial }}
                </div>
              </div>

              <!-- USER INFO -->
              <div class="user-info" v-if="name">
                <div class="name font-weight-bold brand_navy text-capitalize">
                  {{ name }}
                </div>
                <div class="class-name border_grey_dark">{{ class_name }}</div>
              </div>

              <div class="default-user-info" v-else>
                <div
                  class="name rounded-10 skeleton-loader overflow-hidden"
                ></div>
                <div
                  class="class-name rounded-10 skeleton-loader overflow-hidden"
                ></div>
              </div>
            </div>
          </div>

          <!-- TEACHER SUBJECT -->
          <div class="ignore" v-if="modal_type === 'assign'">
            <Multiselect
              label_text="Assign Subject(s)"
              :label_compact="true"
              :options="subject_options"
              :trigger_close="close_select"
              :multiple="true"
              placeholder="Assign subject(s)"
              @selectionDone="updateSubjectList($event)"
            ></Multiselect>
          </div>

          <!-- TEACHER SUBJECT SECTION -->
          <div
            class="teacher-subject-section d-flex justify-content-start align-items-start flex-wrap rounded-5"
            v-if="modal_type === 'remove'"
          >
            <div
              class="subject-holder"
              :class="subject.selected ? 'remove-subject' : null"
              v-for="(subject, index) in subject_list"
              :key="index"
            >
              {{ subject.name }}
              <div
                class="avatar ml-2"
                @dblclick="unSelectSubject(subject.id)"
                @click="selectSubject(subject.id)"
              >
                <span class="icon-minus font-15 color_ash flex-center"></span>
              </div>
            </div>
          </div>
          <!-- <div class="help-text color_grey_dark font-12-5 mt-3">
            <span class="brand_red">Note:</span> Double click to revert subject
            selections
          </div> -->
          <!-- TEACHER SUBJECT SECTION -->

          <div class="modal-body-row row mt-4 mb-1">
            <div class="col-12 d-flex flex-center">
              <button
                type="submit"
                class="btn btn-md transparent-bg z-depth-0 brand_navy font-weight-bold"
                @click="$emit('closeTriggered')"
              >
                Cancel
              </button>

              <button
                type="submit"
                class="btn btn-md btn-accent"
                ref="confirmBtn"
              >
                Confirm
              </button>
            </div>
          </div>
        </form>
      </div>
    </Modal>
  </div>
</template>

<script>
import { mapGetters, mapActions } from "vuex";
import { colors, random, shuffle, setInitial } from "@/scripts/utilities";
import Modal from "@/components/globalComps/Modal";
import Multiselect from "@/components/globalComps/Multiselect";
import { setTimeout } from "timers";

export default {
  name: "AssignClassModal",

  components: {
    Modal,
    Multiselect,
    AuthAlertCard: () =>
      import(
        /* webpackChunkName: "AuthAlertCard" */ "@/components/globalComps/AuthComps/AuthAlertCard.vue"
      ),
  },

  props: {
    teacher_data: {},
  },

  computed: {
    ...mapGetters(["getCurrentClass"]),

    setAvatarBg() {
      let shuffled = shuffle(colors);
      return shuffled[random(15, 0)];
    },

    getInitial() {
      return setInitial(this.name);
    },
  },

  watch: {
    teacher_data: {
      handler(data) {
        if (data.subjects.length)
          this.form.subjects = data.subjects.map((subject) => subject.id);
      },
      immediate: true,
    },
  },

  data() {
    return {
      form: {
        teacher_id: "",
        class_id: this.teacher_data.class_id,
        subjects: [],
      },
      value: [],
      name: "",
      email: "",
      image: "",
      class_name: "",
      class_options: [],
      subject_options: [],

      show_alert: false,
      alert_type: null, //SET TO EITHER SUCCESS OR ERROR
      alert_msg: null,
      close_select: true,
      modal_type: this.teacher_data.modal_type || "assign",
      subject_list: this.teacher_data.subjects || [],
    };
  },

  mounted() {
    this.fetchTeacherInfo();
    this.getClassName();
    this.modal_type === "assign" ? this.fetchSubjects() : null;
  },

  methods: {
    ...mapActions([
      "getTeacherProfile",
      "getSubjectList",
      "assignSubjectOnly",
      "removeTeacherClassSubject",
      "getSingleClass",
    ]),

    closeMultiSelect($event) {
      if ($event.target.classList.contains("ignore")) return false;
      else this.close_select = !this.close_select;
    },

    selectSubject(id) {
      this.subject_list.map((subject) => {
        if (subject.id === id) subject.selected = true;
      });
    },

    unSelectSubject(id) {
      this.subject_list.map((subject) => {
        if (subject.id === id) subject.selected = false;
      });
    },

    fetchTeacherInfo() {
      this.form.teacher_id = Number(this.teacher_data.teacher_id);
      this.getTeacherProfile(this.teacher_data.teacher_id)
        .then((response) => {
          this.name = `${response.data.firstname} ${response.data.lastname}`;
          this.image = response.data.image;
        })
        .catch((err) => console.log(err));
    },

    getClassName() {
      this.getSingleClass(this.teacher_data.class_id)
        .then((response) => {
          response.code !== 200 ? this.getClassName : null;
          this.class_name = response.data.class_name;
        })
        .catch((err) => err);
    },

    fetchSubjects() {
      this.getSubjectList()
        .then((response) => {
          if (response.data.length > 0) {
            response.data.map((subject) => {
              let is_assigned = this.teacher_data.subjects.find(
                (sub) => Number(sub.id) === Number(subject.subject_id)
              );

              let subject_data = {};

              subject_data.id = subject.subject_id;
              subject_data.name = subject.name;

              subject_data.selected = is_assigned ? true : false;

              this.subject_options.push(subject_data);
            });
          } else {
            this.subject_options.push({});
          }
        })
        .catch((err) => console.log(err));
    },

    updateSubjectList($event) {
      this.form.subjects = [];
      $event.selection.map((subject) => {
        this.form.subjects.push(Number(subject.id));
      });
    },

    logErrorAlert(message) {
      setTimeout(() => {
        this.show_alert = true;
        this.alert_type = "error";
        this.alert_msg = message;

        this.$refs.confirmBtn.innerText = "Confirm";
        this.$refs.confirmBtn.disabled = false;
      }, 1000);

      setTimeout(() => {
        this.show_alert = false;
      }, 5000);
    },

    confirmSelections() {
      this.$refs.confirmBtn.disabled = true;
      this.$refs.confirmBtn.innerHTML =
        '<div class="icon-dotted-roller font-17 mr-2 animate"></div>Please wait...';

      this.modal_type === "assign"
        ? this.confirmAssignSubjects()
        : this.confirmRemoveSubjects();
    },

    confirmAssignSubjects() {
      if (!this.form.subjects.length) {
        this.logErrorAlert("Please select at least one subject");
        return;
      }
      this.assignSubjectOnly(this.form)
        .then((response) => {
          if (response.code === 200) {
            setTimeout(() => {
              this.show_alert = false;

              this.$refs.confirmBtn.disabled = true;
              this.$refs.confirmBtn.innerHTML =
                '<div class="icon-accept font-18 mr-2"></div>Assigned';
            }, 1000);

            setTimeout(() => {
              let dialog_data = {
                msg: "Subject successfully assigned to teacher!",
                type: "success",
              };
              this.$emit("showAlertCard", dialog_data);
              this.$emit("closeTriggered");
            }, 3000);
          }

          //   HANDLE NON 200 RESPONSE
          else this.logErrorAlert(response.message);
        })
        .catch((err) => console.log(err));
    },

    confirmRemoveSubjects() {
      this.form.subjects = [];
      this.subject_list.map((subject) => {
        if (subject.selected) this.form.subjects.push(subject.id);
      });

      console.log(this.form);

      this.removeTeacherClassSubject(this.form)
        .then((response) => {
          console.log(response);
          if (response.code === 200) {
            setTimeout(() => {
              this.show_alert = false;

              this.$refs.confirmBtn.disabled = true;
              this.$refs.confirmBtn.innerHTML =
                '<div class="icon-accept font-18 mr-2"></div>Removed';
            }, 1000);

            setTimeout(() => {
              let dialog_data = {
                msg: "Subject removed successfully!",
                type: "success",
              };
              this.$emit("showAlertCard", dialog_data);
              this.$emit("closeTriggered");
            }, 3000);
          }

          // HANDLE NON 200 RESPONSE
          else this.logErrorAlert(response.message);
        })
        .catch((err) => console.log(err));
    },
  },
};
</script>

<style lang="scss" scoped>
.user-info-section {
  .user-info {
    transition: all 0.4s ease-in-out;
    .name {
      font-size: 19px;
      line-height: 25px;
      margin-bottom: 4px;
    }
    .class-name {
      font-size: 14px;
      line-height: 19px;
    }
  }

  .default-user-info {
    .name {
      width: 200px;
      height: 25px;
      margin-bottom: 5px;
    }
    .class-name {
      width: 100px;
      height: 15px;
    }
  }
}

.teacher-subject-section {
  border: 1px solid $border_grey;
  padding: 10px;

  .subject-holder {
    margin-right: 10px;
    margin-bottom: 10px;
    border: 1px solid $brand_inverse;
    background: $brand_inverse_light;
    @include flex_row_center_nowrap;
    font-size: 12.5px;
    line-height: 17px;
    color: $color_text;
    border-radius: 30px;
    padding: 5px 12px;
    white-space: nowrap;

    .avatar {
      @include equal_min_edge(26);
      border: 1px solid $brand_accent;
      background: $brand_accent_light;
      transition: all 0.4s ease-in-out;
      cursor: pointer;
      &:hover {
        background: $brand_red_light;
      }
    }
  }

  .remove-subject {
    border: 1px solid $brand_red;
    background: $brand_red_light;
  }
}
</style>
